import { Decimal } from 'decimal.js';
import * as Enumerable from 'linq';
import { MobileAllocationSummary } from '../entities/scheduler/mobile.allocations';
import { TaxRate } from '../entities/taxrate/tax.rate';
import { TenantUser } from '../entities/tenant/user';
import { Job } from '../entities/job/job';
import { SystemWorkOrderStatus } from '../entities/job/system.workorder.status';
import { WorkOrder } from '../entities/job/workorder';
import { WorkOrderStatus } from '../entities/job/workorder.status';
import { EmbeddedCode, CodeStatus, CodeInvoiceStatus } from '../entities/shared/code';
import { WorkOrderUpdateResponse } from '../responses/job/workorder.update.response';
import { WorkOrderStatusUtility } from './workorder.status.utility'
import { v4 } from 'uuid';

export class WorkOrderUtility {


    public static compareStatus(status: WorkOrderStatus, statusName: string): boolean {
        if (!status)
            return false;

        return status.systemName === statusName;
    }

    public static compareSystemStatus(status: WorkOrderStatus, statusName: string): boolean {
        return WorkOrderUtility.systemStatusCompare(status,
            statusName as SystemWorkOrderStatus);
    }

    public static isSystemStatusOpen(status: WorkOrderStatus): boolean {
        return WorkOrderUtility.systemStatusCompare(status, SystemWorkOrderStatus.Open);
    }

    public static isSystemStatusClosed(status: WorkOrderStatus): boolean {
        return WorkOrderUtility.systemStatusCompare(status, SystemWorkOrderStatus.Closed);
    }

    public static isSystemStatusFinalised(status: WorkOrderStatus): boolean {
        return WorkOrderUtility.systemStatusCompare(status, SystemWorkOrderStatus.Finalised);
    }

    public static isSystemStatusCompleted(status: WorkOrderStatus): boolean {
        return WorkOrderUtility.systemStatusCompare(status, SystemWorkOrderStatus.Completed);
    }

    public static isSystemStatusInProgress(status: WorkOrderStatus): boolean {
        return WorkOrderUtility.systemStatusCompare(status, SystemWorkOrderStatus.InProgress);
    }

    public static isSystemStatusOnHold(status: WorkOrderStatus): boolean {
        return WorkOrderUtility.systemStatusCompare(status, SystemWorkOrderStatus.OnHold);
    }

    private static systemStatusCompare(status: WorkOrderStatus, systemStatus: SystemWorkOrderStatus): boolean {
        if (!status)
            return false;
        if (!systemStatus)
            return false;
        return status.systemStatus ? status.systemStatus === systemStatus : false;
    }

    public static sortCodes<T extends EmbeddedCode>(codes: T[], excludeCancelled = false): T[] {

        if (!codes) return [];

        return Enumerable
            .from(codes)
            .where(x => excludeCancelled
                ? x.status !== CodeStatus.Cancelled
                : true)
            .select(x => {
                x.location = x.location ? x.location : '';
                x.sortOrder = WorkOrderUtility.getCodeSortOrder(x.status);
                return x;
            })
            .orderBy(x => x.sortOrder)
            .thenBy(x => x.location)
            .thenBy(x => x.name)
            .toArray();
    }

    public static clientCodes<T extends EmbeddedCode>(codes: T[]): T[] {

        if (!codes) return [];

        return Enumerable
            .from(codes)
            .where(x => {
                const validStatus = [CodeStatus.Normal, CodeStatus.Variation].includes(x.status);
                const validInvoicing = x.codeInvoicingStatus !== CodeInvoiceStatus.NotBillable;
                return validStatus && validInvoicing;
            })
            .select(x => {
                if (x.codeInvoicingStatus === CodeInvoiceStatus.NoCharge) {
                    x.sellPrice = 0;
                }
                x.location = x.location || '';
                x.sortOrder = WorkOrderUtility.getCodeSortOrder(x.status);
                return x;
            })
            .orderBy(x => x.sortOrder)
            .thenBy(x => x.location)
            .thenBy(x => x.name)
            .toArray();
    }

    public static addCodesUoM(codes: EmbeddedCode[], excludeCancelled = false): EmbeddedCode[] {

        if (!codes) return [];

        return Enumerable
            .from(codes)
            .where(x => excludeCancelled
                ? x.status !== CodeStatus.Cancelled
                : true)
            .select(x => {
                //x.uoms.push({ conversionRate: 1, uom: x.baseUoM })
                const code = { ...x };
                return code;
            })
            .toArray();
    }

    public static getCodeSortOrder(status: CodeStatus): number {
        status = status ? status : CodeStatus.Normal;
        let sortOrder = 0;
        switch (status) {
            case CodeStatus.Variation:
                sortOrder = 20;
                break;
            case CodeStatus.Cancelled:
                sortOrder = 30;
                break;
            default:
                sortOrder = 10;
                break;
        }
        return sortOrder;
    }

    public static getBillTotal(workOrder: WorkOrder, taxRate: TaxRate): InvoiceTotals {

        let total = 0;
        let subTotal = 0;
        let gst = 0;

        const taxRateValue = taxRate
            ? taxRate.rate / 100
            : 0;

        Enumerable
            .from(WorkOrderUtility.sortCodes(workOrder.codes, true))
            .forEach(p => {
                const price = p.buyPrice ? p.buyPrice : 0;
                const amountExGST = (price * p.quantity);
                const amountGST = amountExGST * taxRateValue;
                // const final = amountExGST + amountGST;
                // total += final;
                subTotal += amountExGST;
                gst += amountGST;
            });

        subTotal = new Decimal(subTotal).toDecimalPlaces(2).toNumber();
        gst = new Decimal(gst).toDecimalPlaces(2).toNumber();
        total = subTotal + gst;

        return {
            subTotal: subTotal || 0,
            tax: gst || 0,
            total: total || 0
        };
    }

    public static getInvoiceTotal(job: Job, taxRate: TaxRate): InvoiceTotals {

        let total = 0;
        let subTotal = 0;
        let gst = 0;

        const taxRateValue = taxRate
            ? taxRate.rate / 100
            : 0;

        Enumerable
            .from(job.workOrders)
            .forEach(workOrder => {

                Enumerable
                    .from(WorkOrderUtility.sortCodes(workOrder.codes, true))
                    .forEach(p => {
                        const price = p.sellPrice ? p.sellPrice : 0;
                        const amountExGST = (price * p.quantity);
                        const amountGST = amountExGST * taxRateValue;
                        // const final = amountExGST + amountGST;
                        // total += final;
                        subTotal += amountExGST;
                        gst += amountGST;
                    });

            });

        subTotal = new Decimal(subTotal).toDecimalPlaces(2).toNumber();
        gst = new Decimal(gst).toDecimalPlaces(2).toNumber();
        total = subTotal + gst;

        return {
            subTotal: subTotal || 0,
            tax: gst || 0,
            total: total || 0
        };
    }

    public static getWorkOrderWithVersion(workOrder: WorkOrder) {
        if (!workOrder.version) return `${workOrder.workOrderNumber} (V1)`;

        return `${workOrder.workOrderNumber} (V${workOrder.version})`;
    }

    public static convertWorkOrderToResponse(workOrder: WorkOrder, allocation?: MobileAllocationSummary): WorkOrderUpdateResponse {

        if (!workOrder) return null;

        const status = WorkOrderStatusUtility.convertToValidStatus(workOrder?.status);

        return {
            jobId: workOrder?.jobId,
            workOrderId: workOrder?._id,
            name: workOrder?.name,
            status: status,
            dueDate: workOrder?.dueDate,
            startDate: workOrder?.startDate,
            description: workOrder.description,
            technician: workOrder?.technician,
            subcontractor: workOrder?.subcontractor,
            type: workOrder.jobType,
            owner: workOrder.owner,
            customerPO: workOrder.customerWO,
            customerWO: workOrder.customerWO,
            updatedAt: workOrder.updatedAt,
            customFields: workOrder.customFields,
            alternateSite: workOrder.alternateSite,
            resources: workOrder.resources,
            allocation: allocation
        };

    }

    /**
     * Use for converting Quote / Opportunity to WorkOrder
     * 
     * This logic is merged from ```JobTemplateUtility.getJobTemplateCode(JobTemplateUtility.getJobCode(i))```
     */
    public static buildCodes(codes: EmbeddedCode[], options?: { keepId?: boolean, costOnly?: boolean }): EmbeddedCode[] {

        const keepId = options?.keepId ?? false;

        const result = Enumerable
            .from(codes)
            .where(x => !!x)
            .select(x => {
                const code: EmbeddedCode = {
                    _id: keepId ? x._id : v4(),

                    sellPrice: x.adjustedSell,
                    customSellPrice: true,
                    codeInvoicingStatus: CodeInvoiceStatus.Billable,

                    name: x.name,
                    quantity: x.quantity,
                    location: x.location,
                    description: x.description,
                    longDescription: x.longDescription,
                    canOverrideDesc: x.canOverrideDesc,
                    category: x.category,
                    codeUoM: x.codeUoM,
                    buyPrice: x.buyPrice,
                    customBuyPrice: x.customBuyPrice,
                    note: x.note,
                    suppliedBySubcontractor: x.suppliedBySubcontractor,
                    type: x.type,
                };

                if (options?.costOnly === true) {
                    code.customSellPrice = false;
                    code.sellPrice = 0;
                    code.codeInvoicingStatus = CodeInvoiceStatus.NotBillable;
                }

                return code;
            })
            .toArray();

        return result;

    }
}

export interface InvoiceTotals {
    subTotal: number;
    tax: number;
    total: number;
}
